import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import { VrsChatStatus } from 'features/vrs-chat/enums';
import type { VrsChatState } from 'features/vrs-chat/interfaces';
import { VrsChatOpenedBy } from 'features/vrs-chat/interfaces';

export const initialVrsChatState: VrsChatState = {
  isVisible: false,
  status: VrsChatStatus.Disconnected,
  isCollapsed: false,
  openedBy: null,
};

const vrsChatSlice = createSlice({
  initialState: initialVrsChatState,
  name: 'vrs-chat',
  reducers: {
    toggleVrsChatVisibility: (state) => {
      if (!state.isVisible) {
        // this is clicked on the VRS header button, so set openedBy to agent
        state.openedBy = VrsChatOpenedBy.Agent;
      } else {
        state.openedBy = null;
        state.isCollapsed = false; // Reset collapse when closing
      }
      state.isVisible = !state.isVisible;
    },
    closeVrsChatPopup: (state) => {
      state.isVisible = false;
      state.openedBy = null;
      state.isCollapsed = false;
    },
    openVrsChatPopupByAgent: (state) => {
      state.isVisible = true;
      state.openedBy = VrsChatOpenedBy.Agent;
    },
    openVrsChatPopupByMessage(state) {
      state.isVisible = true;
      state.openedBy = VrsChatOpenedBy.Message;
    },
    setVrsChatStatus(state, action: PayloadAction<VrsChatStatus>) {
      state.status = action.payload;
    },
    toggleVrsChatCollapse: (state) => {
      state.isCollapsed = !state.isCollapsed;
      // When uncollapsing, set openedBy to 'agent' so input will focus
      if (!state.isCollapsed) {
        state.openedBy = VrsChatOpenedBy.Agent;
      }
    },
    resetVrsChatState: () => initialVrsChatState,
  },
});

export const {
  toggleVrsChatVisibility,
  closeVrsChatPopup,
  resetVrsChatState,
  openVrsChatPopupByMessage,
  openVrsChatPopupByAgent,
  setVrsChatStatus,
  toggleVrsChatCollapse,
} = vrsChatSlice.actions;

export const isVrsChatVisibleSelector = ({ vrsChat }: RootState) =>
  vrsChat.isVisible;

export const selectVrsChatStatus = ({ vrsChat }: RootState) => vrsChat.status;

export const isVrsChatCollapsedSelector = ({ vrsChat }: RootState) =>
  vrsChat.isCollapsed;

export const vrsChatOpenedBySelector = ({ vrsChat }: RootState) =>
  vrsChat.openedBy;

export const vrsChatReducer = vrsChatSlice.reducer;
